/**
 * AvatarFrame — Liquid glass wrapper around the avatar video/canvas.
 *
 * Wraps the PixiJS canvas and idle video in a glass-bordered container
 * with status indicator, glow effects, and spring-animated state transitions.
 */

import { createGlassPanel } from '../glass'
import { springEnter, springExit, springAnimate, type SpringAnimation } from '../spring'
import { injectTokens } from '../tokens'
import { autoDetectAndApply, type GlassTheme } from '../themeDetector'

export type AvatarStatus = 'idle' | 'listening' | 'thinking' | 'speaking' | 'connecting'

const STATUS_COLORS: Record<AvatarStatus, string> = {
  idle: 'var(--hs-text-tertiary)',
  listening: '#34C759',      // green
  thinking: '#FF9F0A',       // amber
  speaking: 'var(--hs-accent)',
  connecting: 'var(--hs-text-tertiary)',
}

const STATUS_LABELS: Record<AvatarStatus, string> = {
  idle: '',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Speaking',
  connecting: 'Connecting',
}

export class AvatarFrame {
  readonly el: HTMLDivElement
  private statusDot: HTMLDivElement
  private statusLabel: HTMLSpanElement
  private statusBar: HTMLDivElement
  private glowRing: HTMLDivElement
  private pulseAnim: SpringAnimation | null = null
  private currentStatus: AvatarStatus = 'idle'
  private theme: GlassTheme = 'dark'

  constructor() {
    injectTokens()

    // Outer glass frame
    this.el = createGlassPanel({
      radius: 'lg',
      elevation: 'base',
      className: 'hs-avatar-frame',
    }) as HTMLDivElement

    Object.assign(this.el.style, {
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    })

    // Glow ring — subtle colored border glow during active states
    this.glowRing = document.createElement('div')
    Object.assign(this.glowRing.style, {
      position: 'absolute',
      inset: '-1px',
      borderRadius: 'inherit',
      border: '2px solid transparent',
      pointerEvents: 'none',
      transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
      zIndex: '2',
    })
    this.el.appendChild(this.glowRing)

    // Status bar — bottom overlay with dot + label
    this.statusBar = document.createElement('div')
    Object.assign(this.statusBar.style, {
      position: 'absolute',
      bottom: '0',
      left: '0',
      right: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--hs-space-2)',
      padding: 'var(--hs-space-2) var(--hs-space-3)',
      background: 'linear-gradient(transparent, rgba(0,0,0,0.3))',
      zIndex: '3',
      opacity: '0',
      transition: 'opacity 0.2s ease',
      pointerEvents: 'none',
    })

    this.statusDot = document.createElement('div')
    Object.assign(this.statusDot.style, {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: STATUS_COLORS.idle,
      transition: 'background 0.3s ease, box-shadow 0.3s ease',
      flexShrink: '0',
    })

    this.statusLabel = document.createElement('span')
    Object.assign(this.statusLabel.style, {
      fontSize: 'var(--hs-text-xs)',
      fontFamily: 'var(--hs-font)',
      fontWeight: 'var(--hs-weight-medium)',
      color: 'rgba(255,255,255,0.8)',
      letterSpacing: '0.02em',
    })

    this.statusBar.appendChild(this.statusDot)
    this.statusBar.appendChild(this.statusLabel)
    this.el.appendChild(this.statusBar)
  }

  /** Set the size of the frame. */
  setSize(width: number, height: number): void {
    this.el.style.width = `${width}px`
    this.el.style.height = `${height}px`
  }

  /** Append a child element (video, canvas) into the frame. */
  appendContent(child: HTMLElement): void {
    // Insert before the glow ring and status bar (they should overlay)
    this.el.insertBefore(child, this.glowRing)
  }

  /** Update the avatar status — changes glow, dot, and label. */
  setStatus(status: AvatarStatus): void {
    if (status === this.currentStatus) return
    this.currentStatus = status

    const color = STATUS_COLORS[status]
    const label = STATUS_LABELS[status]

    // Status dot
    this.statusDot.style.background = color
    this.statusDot.style.boxShadow = status !== 'idle' ? `0 0 6px ${color}` : 'none'

    // Status label
    this.statusLabel.textContent = label

    // Status bar visibility
    this.statusBar.style.opacity = status === 'idle' ? '0' : '1'

    // Glow ring
    if (status === 'speaking' || status === 'listening') {
      this.glowRing.style.borderColor = color
      this.glowRing.style.boxShadow = `0 0 16px ${color}40, inset 0 0 16px ${color}10`
      this.startPulse(color)
    } else {
      this.glowRing.style.borderColor = 'transparent'
      this.glowRing.style.boxShadow = 'none'
      this.stopPulse()
    }
  }

  /** Detect and apply the correct glass theme based on background. */
  detectTheme(): GlassTheme {
    this.theme = autoDetectAndApply(this.el)
    return this.theme
  }

  /** Manually set the theme. */
  setTheme(theme: GlassTheme): void {
    this.theme = theme
    this.el.setAttribute('data-hs-theme', theme)
  }

  /** Spring-animated entrance. */
  show(): SpringAnimation {
    this.el.style.display = 'flex'
    return springEnter(this.el, { preset: 'gentle' })
  }

  /** Spring-animated exit. */
  hide(remove = false): SpringAnimation {
    return springExit(this.el, { remove, preset: 'smooth' })
  }

  private startPulse(color: string): void {
    this.stopPulse()
    let direction = 1
    let value = 0

    this.pulseAnim = springAnimate({
      from: 0,
      to: 1,
      preset: 'gentle',
      onUpdate: (v) => {
        const intensity = 0.15 + v * 0.25
        this.glowRing.style.boxShadow =
          `0 0 ${16 + v * 8}px ${color}${Math.round(intensity * 255).toString(16).padStart(2, '0')}, ` +
          `inset 0 0 ${16 + v * 4}px ${color}10`
      },
      onComplete: () => {
        // Reverse — create gentle pulsing loop
        if (this.currentStatus === 'speaking' || this.currentStatus === 'listening') {
          this.startPulse(color)
        }
      },
    })
  }

  private stopPulse(): void {
    this.pulseAnim?.stop()
    this.pulseAnim = null
  }

  destroy(): void {
    this.stopPulse()
    this.el.remove()
  }
}
